import { useState } from "react";
import { Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { RestartAlt } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import api from "../services/api";

// The Test Editor's step dialog fields for visualCheck and accessibilityCheck steps
// (the selector field above them is optional for both: empty = the whole page).

const lines = (text) =>
	text
		.split("\n")
		.map((s) => s.trim())
		.filter(Boolean);

function VisualFields({ step, onChange, testId, readOnly }) {
	const { enqueueSnackbar } = useSnackbar();
	const visual = step.visual || {};
	const set = (patch) => onChange({ ...step, visual: { ...visual, ...patch } });
	const [masks, setMasks] = useState((visual.maskSelectors || []).join("\n"));
	const [resetting, setResetting] = useState(false);

	const reset = async () => {
		setResetting(true);
		try {
			const { data } = await api.delete(`/tests/${testId}/steps/${step.id}/baselines`);
			enqueueSnackbar(
				data.deleted ? "Baseline reset: the next run saves a new one" : "This check has no baseline yet",
				{ variant: "success" },
			);
		} catch (err) {
			enqueueSnackbar(err.response?.data?.message || "Couldn't reset the baseline", { variant: "error" });
		} finally {
			setResetting(false);
		}
	};

	return (
		<>
			<Typography variant="body2" color="text.secondary">
				Compares a screenshot with the baseline saved on this check's first run. Leave the selector empty to check the page.
			</Typography>
			<FormControlLabel
				control={<Checkbox checked={!!visual.fullPage} disabled={!!step.selector} onChange={(e) => set({ fullPage: e.target.checked || undefined })} />}
				label="Whole page, not just the visible part"
			/>
			<TextField
				label="Allowed difference (% of pixels)"
				type="number"
				fullWidth
				value={visual.maxDiffPercent ?? ""}
				placeholder="0.1"
				onChange={(e) => set({ maxDiffPercent: e.target.value === "" ? undefined : Number(e.target.value) })}
				inputProps={{ min: 0, max: 100, step: 0.05 }}
				helperText="The check fails when more of the screenshot changed than this (default 0.1%)"
			/>
			<TextField
				label="Ignore these regions (one selector per line)"
				fullWidth
				multiline
				minRows={2}
				value={masks}
				onChange={(e) => {
					setMasks(e.target.value);
					set({ maskSelectors: lines(e.target.value) });
				}}
				placeholder={".clock\n#ad-banner"}
				helperText="Content that changes on every run (dates, ads, avatars) is blanked out before comparing"
			/>
			{testId && step.id && !readOnly && (
				<Box>
					<Button size="small" startIcon={<RestartAlt />} onClick={reset} disabled={resetting}>
						Reset baseline
					</Button>
					<Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
						The next run saves a new one. To accept a changed screenshot, use the run's “Accept as new baseline”.
					</Typography>
				</Box>
			)}
		</>
	);
}

function AccessibilityFields({ step, onChange }) {
	const a11y = step.accessibility || {};
	const set = (patch) => onChange({ ...step, accessibility: { ...a11y, ...patch } });
	const [ignore, setIgnore] = useState((a11y.ignoreRules || []).join(", "));
	return (
		<>
			<Typography variant="body2" color="text.secondary">
				Scans the page (or the selected element) for WCAG problems with axe-core. Leave the selector empty to scan the page.
			</Typography>
			<Box sx={{ display: "flex", gap: 2 }}>
				<FormControl fullWidth>
					<InputLabel>WCAG level</InputLabel>
					<Select label="WCAG level" value={a11y.level || "AA"} onChange={(e) => set({ level: e.target.value })}>
						<MenuItem value="A">A</MenuItem>
						<MenuItem value="AA">AA (recommended)</MenuItem>
						<MenuItem value="AAA">AAA</MenuItem>
					</Select>
				</FormControl>
				<FormControl fullWidth>
					<InputLabel>Fail on</InputLabel>
					<Select label="Fail on" value={a11y.failOn || "serious"} onChange={(e) => set({ failOn: e.target.value })}>
						<MenuItem value="critical">Critical only</MenuItem>
						<MenuItem value="serious">Serious or worse</MenuItem>
						<MenuItem value="moderate">Moderate or worse</MenuItem>
						<MenuItem value="minor">Any violation</MenuItem>
					</Select>
				</FormControl>
			</Box>
			<TextField
				label="Rules to ignore (optional)"
				fullWidth
				value={ignore}
				onChange={(e) => {
					setIgnore(e.target.value);
					set({
						ignoreRules: e.target.value
							.split(",")
							.map((s) => s.trim())
							.filter(Boolean),
					});
				}}
				placeholder="color-contrast, region"
				helperText="axe rule ids, comma separated. Violations below the “fail on” level are still reported on the run."
			/>
		</>
	);
}

export default function CheckStepFields(props) {
	if (props.step.type === "visualCheck") return <VisualFields {...props} />;
	if (props.step.type === "accessibilityCheck") return <AccessibilityFields {...props} />;
	return null;
}
